const jsonToMd = (jsonString: string) => {
  // Validate JSON
  let obj: unknown;
  try {
    obj = JSON.parse(jsonString);
  } catch {
    return null;
  }

  const urlPattern = /https?:\/\/[^\s<>"{}|\\^`[\]]+/g;
  const emailPattern = /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/g;

  const isDateString = (value: string): boolean => {
    const isoDateRegex = /^\d{4}-\d{2}-\d{2}(T\d{2}:\d{2}:\d{2}(\.\d{3})?(Z|[+-]\d{2}:\d{2})?)?$/;
    return isoDateRegex.test(value) && !Number.isNaN(Date.parse(value));
  };

  const linkifyText = (text: string): string => {
    // First, linkify URLs
    let result = text.replace(urlPattern, url => `[${url}](${url})`);
    // Then, linkify emails (but not if already part of a markdown link)
    result = result.replace(emailPattern, (email, offset, str) => {
      // Check if this email is already inside a markdown link
      const before = str.slice(0, offset);
      const isInsideLink = before.lastIndexOf('[') > before.lastIndexOf(')');
      if (isInsideLink) return email;
      return `[${email}](mailto:${email})`;
    });
    return result;
  };

  const formatValue = (value: unknown): string => {
    if (value === null) return 'Not provided';
    if (value === undefined) return 'Not provided';
    if (typeof value === 'boolean') return value ? 'Yes' : 'No';
    if (typeof value === 'number') return value.toLocaleString();
    if (typeof value === 'string') {
      if (isDateString(value)) {
        const date = new Date(value);
        return date.toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'long',
          day: 'numeric',
        });
      }
      return linkifyText(value);
    }
    return String(value);
  };

  const humanizeKey = (key: string): string => {
    return key
      .replace(/([A-Z])/g, ' $1') // camelCase → camel Case
      .replace(/[_-]/g, ' ') // snake_case → snake case
      .replace(/([0-9]+)/g, ' $1 ') // add space around numbers
      .replace(/\s+/g, ' ') // clean up multiple spaces
      .trim()
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  const convert = (data: unknown, level = 0): string => {
    const lines: string[] = [];
    const indent = level === 0 ? '' : '   '.repeat(level);
    const bullet = level === 0 ? '' : '- ';

    if (typeof data !== 'object' || data === null) {
      return `${indent}${bullet}${formatValue(data)}`;
    }

    const entries = Object.entries(data as Record<string, unknown>);

    for (const [key, value] of entries) {
      const humanKey = humanizeKey(key);

      if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
        // Nested object - combine header and content with single newline
        lines.push(`${indent}${bullet}**${humanKey}:**\n${convert(value, level + 1)}`);
      } else if (Array.isArray(value)) {
        // Array
        const arrayLines: string[] = [`${indent}${bullet}**${humanKey}:**`];

        if (value.length === 0) {
          arrayLines.push(`${indent}   - _empty_`);
        } else if (typeof value[0] === 'object' && value[0] !== null) {
          // Array of objects
          value.forEach((item, i) => {
            const singularKey = humanKey.endsWith('s') ? humanKey.slice(0, -1) : humanKey;
            arrayLines.push(`${indent}   - **${singularKey} ${i + 1}:**\n${convert(item, level + 2)}`);
          });
        } else {
          // Array of primitives
          value.forEach(item => {
            arrayLines.push(`${indent}   - ${formatValue(item)}`);
          });
        }
        lines.push(arrayLines.join('\n'));
      } else {
        // Primitive value
        lines.push(`${indent}${bullet}**${humanKey}:** ${formatValue(value)}`);
      }
    }

    const separator = level === 0 ? '\n\n' : '\n';
    return lines.join(separator);
  };

  return convert(obj);
};

export default jsonToMd;
